export interface MultiSelectOption {
  value: string;
  label: string;
  /** Display-only decoration. Never contributes to the search key. */
  count?: number;
  disabled?: boolean;
  group?: string;
}

export type MultiSelectMatcher = (text: string, query: string) => boolean;

export const defaultMultiSelectMatcher: MultiSelectMatcher = (text, query) =>
  text.toLocaleLowerCase().includes(query.toLocaleLowerCase());

/**
 * The only strings an option is searchable by. Presentational decoration —
 * `count`, badges, anything a renderer composes — is deliberately excluded:
 * making facet-count digits searchable turned a query for `5` into a match on
 * a substation labelled `28 (15656)`.
 */
export function multiSelectSearchKeys(option: MultiSelectOption): string[] {
  return [option.value, option.label];
}

export function filterMultiSelectOptions<T extends MultiSelectOption>(
  options: readonly T[],
  query: string,
  matcher: MultiSelectMatcher = defaultMultiSelectMatcher
): T[] {
  const trimmed = query.trim();
  if (trimmed === "") return [...options];
  return options.filter((option) => multiSelectSearchKeys(option).some((key) => matcher(key, trimmed)));
}

/**
 * Whether the typed string should be offered as a selectable literal.
 *
 * The trigger is "the query is not already an identity in play" — not "nothing
 * in the list matched". A match-count trigger is defeatable by any incidental
 * substring hit, which is how single-digit values became unreachable in the
 * control this component replaces.
 */
export function shouldOfferLiteralOption(args: {
  query: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
}): boolean {
  const trimmed = args.query.trim();
  if (trimmed === "") return false;

  const normalized = trimmed.toLocaleLowerCase();
  const isExistingOption = args.options.some(
    (option) => option.value.toLocaleLowerCase() === normalized || option.label.toLocaleLowerCase() === normalized
  );
  if (isExistingOption) return false;

  return !args.selected.some((value) => value.toLocaleLowerCase() === normalized);
}

export interface MultiSelectSelectionEntry {
  value: string;
  label: string;
  count?: number;
  /** True when the value is not present in the current `options` set. */
  isUnknown: boolean;
}

/**
 * Selections in insertion order, hydrating labels from `options` where possible.
 * Values with no matching option still produce an entry so URL-hydrated state is
 * never silently dropped.
 */
export function multiSelectSelectionEntries(
  selected: readonly string[],
  options: readonly MultiSelectOption[]
): MultiSelectSelectionEntry[] {
  return selected.map((value) => {
    const option = options.find((candidate) => candidate.value === value);
    if (!option) return { value, label: value, isUnknown: true };
    return option.count === undefined
      ? { value, label: option.label, isUnknown: false }
      : { value, label: option.label, count: option.count, isUnknown: false };
  });
}
